import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mail, CheckCircle2, Newspaper } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import SEOHelmet from "@/components/SEOHelmet";

const newsletterSchema = z.object({
  email: z.string().email({ message: "Bitte geben Sie eine gültige E-Mail-Adresse ein" }),
  name: z.string().optional(),
  privacy: z.boolean().refine((val) => val === true, {
    message: "Bitte akzeptieren Sie die Datenschutzerklärung",
  }),
});

type NewsletterFormData = z.infer<typeof newsletterSchema>;

const Newsletter = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const form = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      email: "",
      name: "",
      privacy: false,
    },
  });

  const onSubmit = async (data: NewsletterFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("newsletter-subscribe", {
        body: {
          email: data.email,
          name: data.name || null,
          source: "website",
        },
      });

      if (error) throw error;

      setIsSuccess(true);
      toast({
        title: "Erfolgreich angemeldet!",
        description: "Sie erhalten ab jetzt unseren Newsletter.",
      });
      form.reset();
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: error.message || "Anmeldung fehlgeschlagen. Bitte versuchen Sie es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHelmet />

      <Navigation />
        <PageBreadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Newsletter" }
        ]} />
        
        {/* Wellenförmiger Hintergrund */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
        </div>

        <div className="min-h-screen flex flex-col relative z-10">
        
        {/* Hero Section */}
        <ResponsiveSection variant="accent" spacing="lg">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
              <Newspaper className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Newsletter für Praxis-Versicherungen
            </h1>
            <p className="text-lg text-muted-foreground">
              Bleiben Sie informiert über Cyberversicherungen, Berufshaftpflicht und alle wichtigen Themen rund um Versicherungen für Arztpraxen
            </p>
          </div>
        </ResponsiveSection>

        {/* Newsletter Benefits */}
        <ResponsiveSection spacing="lg">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">Das erwartet Sie in unserem Newsletter</h2>
            <div className="grid gap-4 mb-12">
              {[
                "Aktuelle Informationen zu Cyberversicherungen für Arztpraxen",
                "Tipps zur Berufshaftpflicht und Betriebshaftpflicht",
                "Neue Tarife und Versicherungsangebote im Überblick",
                "Exklusive Beratungsangebote und Sonderkonditionen",
                "Wichtige Änderungen im Versicherungsrecht",
                "Praxisnahe Fallbeispiele und Empfehlungen"
              ].map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">{benefit}</p>
                </div>
              ))}
            </div>

            {/* Newsletter Form */}
            {!isSuccess ? (
              <div className="bg-card border border-border rounded-lg p-6 sm:p-8">
                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  Jetzt anmelden
                </h3>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      E-Mail-Adresse <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ihre.email@beispiel.de"
                      {...form.register("email")}
                      disabled={isSubmitting}
                    />
                    {form.formState.errors.email && (
                      <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Name (optional)</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Ihr Name"
                      {...form.register("name")}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="privacy"
                      checked={form.watch("privacy")}
                      onCheckedChange={(checked) => form.setValue("privacy", checked as boolean)}
                      disabled={isSubmitting}
                    />
                    <div className="space-y-1">
                      <Label htmlFor="privacy" className="text-sm font-normal cursor-pointer">
                        Ich habe die{" "}
                        <a href="/datenschutz" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                          Datenschutzerklärung
                        </a>{" "}
                        gelesen und akzeptiert. <span className="text-destructive">*</span>
                      </Label>
                      {form.formState.errors.privacy && (
                        <p className="text-sm text-destructive">{form.formState.errors.privacy.message}</p>
                      )}
                    </div>
                  </div>

                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? "Wird gesendet..." : "Zum Newsletter anmelden"}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    Sie können den Newsletter jederzeit wieder abbestellen.
                  </p>
                </form>
              </div>
            ) : (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-8 text-center">
                <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Vielen Dank!</h3>
                <p className="text-muted-foreground mb-6">
                  Sie haben sich erfolgreich für unseren Newsletter angemeldet. Sie erhalten ab jetzt regelmäßig Informationen zu Versicherungen für Arztpraxen.
                </p>
                <Button onClick={() => setIsSuccess(false)} variant="outline">
                  Weitere Anmeldung
                </Button>
              </div>
            )}
          </div>
        </ResponsiveSection>

        <Footer />
        </div>
    </>
  );
};

export default Newsletter;